import { useState, useCallback } from 'react'
import debounce from 'lodash/debounce'

import {
  TAB_KEY,
  DOWN_KEY,
  UP_KEY,
  LEFT_KEY,
  RIGHT_KEY,
  ENTER_KEY,
  ESC_KEY,
} from '../utils/keys'

const useFocusedCell = ({
  columnCount,
  rowCount,
  editableColumn,
}: {
  columnCount: number
  rowCount: number
  editableColumn: boolean[]
}) => {
  const [focusedCell, setFocusedCell] = useState<{
    rowIndex: number
    columnIndex: number
  }>({
    rowIndex: 0,
    columnIndex: 0,
  })

  const [editMode, setEditMode] = useState<boolean>(false)

  const handleKeydown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.keyCode === DOWN_KEY && !editMode) {
        event.preventDefault()
        setFocusedCell((focusedCellState) => ({
          ...focusedCellState,
          rowIndex: Math.min(focusedCellState.rowIndex + 1, rowCount - 1),
        }))
      } else if (event.keyCode === UP_KEY && !editMode) {
        event.preventDefault()
        setFocusedCell((focusedCellState) => ({
          ...focusedCellState,
          rowIndex: Math.max(focusedCellState.rowIndex - 1, 0),
        }))
      } else if (event.keyCode === RIGHT_KEY && !editMode) {
        event.preventDefault()
        setFocusedCell((focusedCellState) => ({
          ...focusedCellState,
          columnIndex: Math.min(
            focusedCellState.columnIndex + 1,
            columnCount - 1,
          ),
        }))
      } else if (event.keyCode === LEFT_KEY && !editMode) {
        event.preventDefault()
        setFocusedCell((focusedCellState) => ({
          ...focusedCellState,
          columnIndex: Math.max(focusedCellState.columnIndex - 1, 0),
        }))
      } else if (event.keyCode === TAB_KEY) {
        event.preventDefault()
        setEditMode(false)
        if (event.shiftKey) {
          setFocusedCell((focusedCellState) => {
            if (focusedCellState.columnIndex === 0) {
              return {
                columnIndex: columnCount - 1,
                rowIndex: (focusedCellState.rowIndex + rowCount - 1) % rowCount,
              }
            }
            return {
              ...focusedCellState,
              columnIndex: focusedCellState.columnIndex - 1,
            }
          })
        } else {
          setFocusedCell((focusedCellState) => {
            if (focusedCellState.columnIndex === columnCount - 1) {
              return {
                columnIndex: 0,
                rowIndex: (focusedCellState.rowIndex + 1) % rowCount,
              }
            }
            return {
              ...focusedCellState,
              columnIndex: focusedCellState.columnIndex + 1,
            }
          })
        }
      } else if (
        event.keyCode === ENTER_KEY &&
        editableColumn[focusedCell.columnIndex]
      ) {
        setEditMode((editModeState) => !editModeState)
      } else if (event.keyCode === ESC_KEY) {
        setEditMode(false)
      }
    },
    [columnCount, rowCount, editMode, focusedCell, editableColumn],
  )

  const navigateTo = useCallback(
    ({
      rowIndex = undefined,
      columnIndex = undefined,
    }: {
      rowIndex?: number
      columnIndex?: number
    }) => () => {
      setFocusedCell((focusedCellState) => {
        if (
          typeof rowIndex === 'number' &&
          typeof columnIndex === 'number' &&
          focusedCellState.rowIndex === rowIndex &&
          focusedCellState.columnIndex === columnIndex &&
          editableColumn[columnIndex]
        ) {
          setEditMode((editModeState) => !editModeState)
        } else {
          setEditMode(false)
        }

        return {
          rowIndex:
            typeof rowIndex === 'number' ? rowIndex : focusedCellState.rowIndex,
          columnIndex:
            typeof columnIndex === 'number'
              ? columnIndex
              : focusedCellState.columnIndex,
        }
      })
    },
    [editableColumn],
  )

  const debounceKeyDownEvent = useCallback(() => {
    const debouncedEvent = debounce(handleKeydown, 40, { maxWait: 40 })
    return (event: React.KeyboardEvent<HTMLTableSectionElement>) => {
      event.persist()

      const blackList = [TAB_KEY, UP_KEY, DOWN_KEY, LEFT_KEY, RIGHT_KEY]
      if (
        (blackList.includes(event.keyCode) && !editMode) ||
        event.keyCode === TAB_KEY
      ) {
        event.preventDefault()
      }

      return debouncedEvent(event)
    }
  }, [handleKeydown, editMode])

  const updateFocusedCellRowIndex = useCallback((rowIndex: number) => {
    setFocusedCell((focusedCellState) => ({ ...focusedCellState, rowIndex }))
  }, [])

  const updateFocusedCellColumnIndex = useCallback((columnIndex: number) => {
    setFocusedCell((focusedCellState) => ({ ...focusedCellState, columnIndex }))
  }, [])

  return {
    focusedCell,
    updateFocusedCellRowIndex,
    updateFocusedCellColumnIndex,
    handleKeydown: debounceKeyDownEvent(),
    navigateTo,
    editMode,
  }
}

export default useFocusedCell
